import React, { useMemo, useRef, useState } from "react";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import { withResizeDetector } from "react-resize-detector";
import whatInput from "../../what-input/source";

import SelectVariantButton from "../Select/SelectVariantButton";
import ArrowIcon from "../ArrowIcon/ArrowIcon";
import DropDownVariantButton from "../DropDown/DropDownVariantButton";
import Button from "../Button/Button";

import { calculateTextDimension } from "../../utils/dom";
import { removeBR } from "../../utils/content/content";
import { CONTENT_CLASSES, FOLDER_TYPE } from "../../utils/content/content.constants";
import { getOptionKey } from "../../utils/select";
import { PopupPositionPattern } from "../Popup/popup.constants";
import { isSystemFolder } from "@/shared/utils";
import { handleKeyDownNavigation } from "../../utils/keyboard-navigation/keyboard-navigation";

import styles from "./BreadCrumbs.module.scss";

const BreadCrumbs = ({ currentLeft, width, maxWidth, onClickItem, items, folderType }) => {
  const { t } = useSharedTranslation();
  const [elRoot, setElRoot] = useState(null);
  const refContentHidden = useRef();

  const currentLeftFinal = currentLeft ?? elRoot?.getBoundingClientRect().left;

  const bottom = "window" in global ? window.innerHeight : 0;
  const boundaryRect = {
    x: currentLeftFinal,
    y: 0,
    top: 0,
    width,
    height: bottom,
    left: currentLeftFinal,
    right: currentLeftFinal + width,
    bottom,
  };

  const [itemsShown, itemsHidden] = useMemo(() => {
    const itemsShown = [];
    const itemsHidden = [];

    let accWidth = 0;
    let itemSize, item;

    for (let i = items.length - 1; i >= 0; i--) {
      itemSize = calculateTextDimension(items[i].label + " > ", ["expandItem"]);
      accWidth += itemSize.width;

      item = {
        index: i,
        icon: undefined,
        ...items[i],
        isTranslateLabel: items[i].isTranslateLabel ?? true,
      };

      if (items.length - 1 === i || accWidth < width) {
        itemsShown.push(item);
      } else {
        itemsHidden.push(item);
      }
    }

    itemsShown.reverse();
    itemsHidden.reverse();

    return [itemsShown, itemsHidden];
  }, [items, width]);

  const renderBcItem = (item, isLast, children, isInsideThreeDots) => {
    const { id, label, title, isTranslateLabel = true, nsSeparator = false } = item;

    let labelFinal;
    if (isSystemFolder(id)) {
      labelFinal = t(title, { defaultValue: title });
    } else if (isTranslateLabel) {
      labelFinal = removeBR(t(label, {
        // prevent labels with a colon (":")
        // being considered as the {[namespace]:[key]}
        nsSeparator: nsSeparator ? undefined : false,
      }))
    } else {
      labelFinal = label;
    }

    const isLastItem = item.index === items.length - 1;
    const isHasChildren = !!children?.length;

    let buttonColor;

    if (isLastItem) {
      if (folderType === FOLDER_TYPE.bible) {
        buttonColor = Button.color.bible;
      } else if (folderType === FOLDER_TYPE.apl) {
        buttonColor = Button.color.apl;
      } else if (folderType === FOLDER_TYPE.reference) {
        buttonColor = Button.color.references;
      } else if (folderType === FOLDER_TYPE.egwwritings) {
        buttonColor = Button.color.egw;
      } else {
        buttonColor = Button.color.icon;
      }
    } else {
      buttonColor = Button.color.icon;
    }

    const buttonProps = {
      tabIndex: -1,
      paddingLess: !isInsideThreeDots,
      fontFamily: isInsideThreeDots ? Button.fontFamily.roboto : undefined,
      variant: Button.variant.clear,
      color: buttonColor,
      style: {
        fontWeight: 400,
        width: isInsideThreeDots ? "100%" : undefined,
        justifyContent: isInsideThreeDots ? "space-between" : undefined,
      },
    };

    buttonProps.onClick = (e, preventDropDownDefault) => {
      if (isHasChildren) {
        if (whatInput.ask() === "mouse" && !isInsideThreeDots) {
          onClickItem(item, item.index);

          if (preventDropDownDefault) {
            preventDropDownDefault();
          }
        }
      } else {
        onClickItem(item, item.index);
      }
    };

    if (!isHasChildren) {
      return (
        <Button key={item.index} className={folderType} {...buttonProps}>
          {labelFinal}&nbsp;
          {!isLastItem && <ArrowIcon direction={ArrowIcon.direction.RIGHT}/>}
        </Button>
      );
    }

    // EGWW-1851 prevent show titles of bible in content
    const childrenFinal = children
      .filter((subItem) => !(
        subItem.className === CONTENT_CLASSES.PARAGRAPH &&
        (subItem.element_type === "h1" || subItem.element_type === "h2")
      ))
      .map((c) => ({
        ...c,
        label: removeBR(t(c.label, {
          // prevent labels with a colon (":")
          // being considered as the {[namespace]:[key]}
          nsSeparator: false,
        })),
      }));

    return (
      <SelectVariantButton
        key={item.index}
        toggleOnMouseOverLeave
        label={labelFinal}
        value={id}
        buttonProps={buttonProps}
        boundaryRect={boundaryRect}
        popupProps={{
          maxHeight: "window" in global ? window.innerHeight * 0.4 : 0,
          positionPattern: isInsideThreeDots ? PopupPositionPattern.AT_RIGHT_ANCHOR_SIDE : undefined,
        }}
        className="js-test_item"
        options={childrenFinal}
        style={{
          minWidth: 0,
        }}
        onChange={(key) => {
          const item = childrenFinal.find((o) => getOptionKey(o) === key);
          onClickItem(item, item.index);
        }}
      />
    );
  };

  const handleKeyDown = (e) => handleKeyDownNavigation(elRoot, e);

  const jsxChildren = [];

  if (itemsHidden?.length) {
    jsxChildren.push(renderBcItem(itemsHidden[0]));

    const handleKeyDown = (e) => handleKeyDownNavigation(refContentHidden.current, e, true);

    if (itemsHidden.length > 1) {
      jsxChildren.push(
        <DropDownVariantButton
          toggleOnMouseOverLeave
          key="threeDots"
          boundaryRect={boundaryRect}
          label="..."
          buttonProps={{
            tabIndex: -1,
          }}
          body={(
            <div ref={refContentHidden} onKeyDown={handleKeyDown}>
              {
                itemsHidden.map((item, index, arr) => {
                  if (index === 0) {
                    return null;
                  }

                  return renderBcItem(
                    item, index === itemsShown.length - 1, arr[index - 1]?.children, true
                  );
                })
              }
            </div>
          )}
        />
      );
    }
  }

  itemsShown.forEach((item, index, arr) => {
    let children;

    if (index === 0) {
      if (itemsHidden?.length) {
        children = itemsHidden[itemsHidden.length - 1].children;
      }
    } else {
      children = arr[index - 1]?.children;
    }

    jsxChildren.push(renderBcItem(item, index === itemsShown.length - 1, children));
  });

  return (
    <div
      ref={setElRoot}
      tabIndex={jsxChildren.length ? 0 : -1}
      className={styles["root"]}
      onKeyDown={handleKeyDown}
      style={maxWidth ? { maxWidth } : undefined}
    >{jsxChildren}</div>
  );
};

export default withResizeDetector(BreadCrumbs);
